import React from 'react';
import { useSearchParams } from 'react-router';
import { useQuery } from '@apollo/client';
import getRedirectQueryString from '../account/getRedirectQueryString';
import { useIsNonVisitorUser } from '../account/useIsLoggedIn';
import EmptyState from '../components/EmptyState';
import { gql } from '../graphql/generated';
import { EntityType } from '../graphql/generated/graphql';

const TOKEN_REFRESH_QUERY = gql(/** @complexity 1 */ `
  query tokenRefresh($id: ID!, $type: EntityType!) {
    entityAssetToken(entityId: $id, entityType: $type) {
      token
    }
  }
`);

const getEntityType = (type: string | null) => {
  switch (type?.toLowerCase()) {
    case 'post':
      return EntityType.Post;
    default:
      return null;
  }
};

const getEntityPath = (type: EntityType, id: string) => {
  switch (type) {
    case EntityType.Post:
      return `/posts/${id}`;
    default:
      return null;
  }
};

interface TokenRefreshRedirectProps {
  assetPath: string;
  type: EntityType;
  id: string;
}

const TokenRefreshRedirect = ({
  assetPath,
  type,
  id,
}: TokenRefreshRedirectProps) => {
  const isNonVisitorUser = useIsNonVisitorUser();

  useQuery(TOKEN_REFRESH_QUERY, {
    fetchPolicy: 'network-only',
    variables: { id, type },
    onCompleted(data) {
      const url = new URL(assetPath, window.__slab.STATIC_ORIGIN);
      url.searchParams.set('jwt', data.entityAssetToken.token);
      window.location.replace(url.toString());
    },
    onError() {
      window.location.replace(
        isNonVisitorUser
          ? (getEntityPath(type, id) ?? '/')
          : `/login${getRedirectQueryString()}`,
      );
    },
  });

  return null;
};

const TokenRefreshPage = () => {
  const [searchParams] = useSearchParams();

  const id = searchParams.get('id');
  const type = getEntityType(searchParams.get('type'));
  const assetPath = searchParams.get('asset_path');

  return assetPath && type && id ? (
    <TokenRefreshRedirect assetPath={assetPath} type={type} id={id} />
  ) : (
    <EmptyState
      title="We couldn't find the asset you are looking for"
      message="The request is missing required parameters"
    />
  );
};

export default TokenRefreshPage;
